import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Formik } from "formik";
import { withRouter } from "react-router-dom";

import { Helmet } from "react-helmet-async";
import styled, { withTheme } from "styled-components/macro";
import {
  Button,
  Grid,
  TextField as MuiTextField,
  Typography as MuiTypography,
  Divider as MuiDivider,
  MenuItem,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import { Alert } from "@material-ui/lab";
import axios from "../../../utils/axios";
import { LOGIN_URL } from "../../../constants/urls";

const TextField = styled(MuiTextField)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Typography = styled(MuiTypography)(spacing);

const CreateButton = styled(Button)`
  padding: 10px 16px;
`;

function FinancialManualForm({ location: { state } }) {
  const {
    user: { jwt },
  } = useSelector((state) => state.authReducer);
  const [companies, setCompanies] = useState([]);
  const [loading, isLoading] = useState(true);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);

  const currentDate = new Date();
  let periods = [];
  for (let i = 0; i < 12; i++) {
    let month;
    let year;
    if (currentDate.getMonth() + i > 12) {
      month = currentDate.getMonth() + i - 12;
      year = currentDate.getFullYear() + 1;
    } else if (currentDate.getMonth() + i === 0) {
      month = 12;
      year = currentDate.getFullYear() - 1;
    } else {
      month = currentDate.getMonth() + i;
      year = currentDate.getFullYear();
    }
    month = month.toString().padStart(2, "0");
    periods.push({
      value: {
        month,
        year,
      },
      text: `${month}/${year}`,
    });
  }

  const loadList = () => {
    isLoading(true);
    setCompanies([]);
    setSuccess(false);
    setError(false);
    axios
      .get(LOGIN_URL + "/companies?active=true", {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      })
      .then((response) => {
        isLoading(false);
        if (response.status === 200) {
          const companiesSorted = response.data.sort((company1, company2) =>
            company1.fantasyName.localeCompare(company2.fantasyName)
          );
          return setCompanies(companiesSorted);
        }
        setError("Não foi possível carregar as empresas");
      })
      .catch((error) => {
        setError(error.message);
      });
  };

  useEffect(loadList, []);

  return (
    <React.Fragment>
      <Helmet title="Financeiro" />

      <Grid container spacing={6} justify="center">
        <Grid item lg={12} xl={9}>
          <Typography variant="h2" gutterBottom display="inline">
            Financeiro
          </Typography>

          <Divider my={6} />
          <Formik
            initialValues={{
              company: "",
              period: "",
              description: "",
              serviceDescription: "",
              value: "",
              paymentType: "",
            }}
            onSubmit={(
              {
                company,
                description,
                serviceDescription,
                value,
                paymentType,
                period: { year, month },
              },
              { setStatus, setErrors }
            ) => {
              const financeToSave = {
                year,
                month,
                companyId: company,
                type: "manual",
                description,
                serviceDescription,
                value,
                paymentType,
              };
              axios
                .post(LOGIN_URL + "/finances/create", financeToSave, {
                  headers: {
                    Authorization: `Bearer ${jwt}`,
                  },
                })
                .then((response) => {
                  if (response.status === 200) {
                    setStatus({ success: true });
                    return;
                  }
                  setStatus({ success: false });
                  // reject(response.data);
                })
                .catch((error) => {
                  setStatus({ success: false });
                  setErrors({ submit: error.error || "Algo deu errado" });
                  // reject(error.message);
                });
            }}
          >
            {(formik) => (
              <form noValidate onSubmit={formik.handleSubmit}>
                {formik.errors.submit && (
                  <Alert mt={2} mb={1} severity="warning">
                    {formik.errors.submit}
                  </Alert>
                )}
                {!formik.errors.submit && formik.status?.success && (
                  <Alert mt={2} mb={1} severity="success">
                    Lançamento manual criado com sucesso.
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xl={12} xs={12}>
                    <TextField
                      type="text"
                      name="company"
                      label="Empresa"
                      fullWidth
                      my={2}
                      select
                      {...formik.getFieldProps("company")}
                    >
                      {companies.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.fantasyName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xl={12} xs={12}>
                    <TextField
                      type="text"
                      name="period"
                      label="Competência"
                      fullWidth
                      my={2}
                      select
                      {...formik.getFieldProps("period")}
                    >
                      {periods.map((period) => (
                        <MenuItem value={period.value}>{period.text}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xl={12} xs={12}>
                    <TextField
                      type="text"
                      name="description"
                      label="Descrição (gerencial)"
                      fullWidth
                      my={2}
                      {...formik.getFieldProps("description")}
                    />
                  </Grid>
                  <Grid item xl={12} xs={12}>
                    <TextField
                      type="text"
                      name="serviceDescription"
                      label="Descrição do serviço (observação da NF)"
                      fullWidth
                      my={2}
                      {...formik.getFieldProps("serviceDescription")}
                    />
                  </Grid>
                  <Grid item xl={12} xs={12}>
                    <TextField
                      type="number"
                      name="value"
                      label={`Valor (formato americano, separador decimal: "." e sem separador de milhar)`}
                      fullWidth
                      my={2}
                      {...formik.getFieldProps("value")}
                    />
                  </Grid>

                  <Grid item xl={12} xs={12}>
                    <TextField
                      type="text"
                      name="paymentType"
                      label="Forma de pagamento"
                      fullWidth
                      my={2}
                      select
                      {...formik.getFieldProps("paymentType")}
                    >
                      <MenuItem value="boleto">Boleto</MenuItem>
                      <MenuItem value="transference">Transferência</MenuItem>
                      <MenuItem value="credit_card">Cartão de crédito</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
                <CreateButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                >
                  Gravar
                </CreateButton>
              </form>
            )}
          </Formik>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default withTheme(withRouter(FinancialManualForm));
